import type { InvestorProfileData } from '@workspace/seedforge-ui/investor-dashboard'
import { InvestorDashboard } from '@workspace/seedforge-ui/investor-dashboard'
import { useCallback } from 'react'
import { InvestorLayout } from '../../components/investor-layout'
import { useInvestorContext } from '../../contexts/investor-guard'
import {
  uploadDocument,
  useCreateDocumentMutation,
  useInvestorConfigQuery,
  useInvestorPersonaQuery,
  useUpdateInvestorMutation,
  useVerifyDocumentMutation,
} from '../../hooks/api'
import { useInviteAcceptance } from '../../hooks/use-invite-acceptance'
import { pitchLink } from '../../lib/pitch-link'

export function InvestorDashboardPage() {
  useInviteAcceptance()
  const { user, investor } = useInvestorContext()
  const { data: investorConfig } = useInvestorConfigQuery()
  const { data: persona } = useInvestorPersonaQuery(investor.id)
  const updateInvestor = useUpdateInvestorMutation(investor.id)
  const createDoc = useCreateDocumentMutation()
  const verifyDoc = useVerifyDocumentMutation()
  const name = user.name ?? 'Investor'
  const pitchLinkUrl = pitchLink(investor.slug)

  const handleProfileSave = (data: InvestorProfileData) => {
    updateInvestor.mutate({
      path: { investorId: investor.id },
      body: {
        name: data.fundName || undefined,
        description: data.fundDescription || undefined,
        websiteUrl: data.websiteUrl || null,
        investmentAreas: data.investmentAreas,
        investmentGeographies: data.investmentGeographies,
      },
    })
  }

  const handleSlugSave = async (newSlug: string): Promise<string | undefined> => {
    try {
      await updateInvestor.mutateAsync({
        path: { investorId: investor.id },
        body: { slug: newSlug },
      })
    } catch {
      return 'This slug is already taken or invalid'
    }
  }

  const handleThesisUpload = useCallback(
    async (file: File) => {
      const { documentId } = await uploadDocument(file, createDoc, verifyDoc)
      await updateInvestor.mutateAsync({
        path: { investorId: investor.id },
        body: { thesisDocumentId: documentId },
      })
    },
    [createDoc, verifyDoc, updateInvestor, investor.id]
  )

  return (
    <InvestorLayout activeNav="Dashboard">
      <InvestorDashboard
        name={name}
        slug={investor.slug}
        pitchLinkUrl={pitchLinkUrl}
        persona={persona}
        fundName={investor.name ?? name}
        fundDescription={investor.description ?? undefined}
        websiteUrl={investor.websiteUrl}
        investmentAreas={investor.investmentAreas ?? []}
        investmentGeographies={investor.investmentGeographies ?? []}
        areaOptions={investorConfig?.investmentAreas}
        regionOptions={investorConfig?.investmentRegions}
        thesisFileName={investor.thesisDocument?.fileName}
        editable
        onProfileSave={handleProfileSave}
        onSlugSave={handleSlugSave}
        onThesisUpload={handleThesisUpload}
      />
    </InvestorLayout>
  )
}
